import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { listWorkspaces } from '../api/endpointsBackend';
import {
  setWorkspaceIdGetter,
  readPersistedWorkspaceId,
  persistWorkspaceId,
} from '../auth/workspaceService';
import { can as canFor } from '../auth/permissions';

const WorkspaceContext = createContext(null);

/**
 * Tracks the user's set of workspaces and which one is currently "active."
 * Registers a getter so the axios interceptor can attach X-Workspace-Id on
 * every outgoing API call (see endpointsBackend.js).
 *
 * Selection rules on first load:
 *   1. If localStorage has an id AND it's still a member workspace, use it.
 *   2. Else pick the personal workspace.
 *   3. Else (no memberships at all) leave null.
 */
export const WorkspaceProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(false);
  // False until the first listWorkspaces() resolves after login. Route guards
  // wait on this so data hooks don't fire before X-Workspace-Id is resolved
  // (the effect below runs *after* the first render, so `loading` alone leaves
  // a gap where the workspace looks "ready" but isn't).
  const [ready, setReady] = useState(false);

  // Register the getter once. The interceptor reads currentId via closure;
  // we re-register whenever currentId changes so it always sees the latest.
  useEffect(() => {
    setWorkspaceIdGetter(() => currentId);
  }, [currentId]);

  useEffect(() => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      setCurrentId(null);
      setReady(false);
      return;
    }
    setLoading(true);
    // What was persisted when the list was asked for. If it changes while the
    // request is in flight, something chose a workspace meanwhile — the
    // invitation page persists the space just joined, which this (older) list
    // does not contain yet — and that choice must survive this answer.
    const persistedAtRequest = readPersistedWorkspaceId();
    listWorkspaces()
      .then((rows) => {
        setWorkspaces(rows);
        const persisted = readPersistedWorkspaceId();
        const stillMember = persisted && rows.some((w) => w.id === persisted);
        // Selection preference order:
        //   1. The persisted workspace IF the user is still a member of it.
        //      Edge case: after a logout / different-user login this would
        //      keep the previous user's workspace selected — but only if the
        //      NEW user is also a member, which is the desired multi-tenant
        //      behaviour ("you stay in the workspace you were last in if it
        //      still applies to you").
        //   2. The workspace where the user is Owner AND it's their personal
        //      one — this is their own private space, the right default for
        //      a brand-new login.
        //   3. Any workspace where they're Owner.
        //   4. Any workspace at all.
        const ownPersonal = rows.find(
          (w) => w.is_personal && w.workspace_role === 'Owner'
        );
        const anyOwned = rows.find((w) => w.workspace_role === 'Owner');
        const chosen = stillMember
          ? persisted
          : ownPersonal?.id ?? anyOwned?.id ?? rows[0]?.id ?? null;
        setCurrentId(chosen);
        if (stillMember || readPersistedWorkspaceId() === persistedAtRequest) {
          persistWorkspaceId(chosen);
        }
      })
      .catch(() => {
        setWorkspaces([]);
        setCurrentId(null);
      })
      .finally(() => {
        setReady(true);
        setLoading(false);
      });
  }, [isAuthenticated]);

  const switchWorkspace = useCallback((id) => {
    if (id === currentId) return;
    persistWorkspaceId(id);
    // Hard reload: data hooks (movements, dashboard, etc.) don't subscribe
    // to currentId, so a soft state change leaves stale rows on screen.
    // Reloading rehydrates everything from the new workspace.
    window.location.reload();
  }, [currentId]);

  const refresh = useCallback(async () => {
    const rows = await listWorkspaces();
    setWorkspaces(rows);
    return rows;
  }, []);

  const current = workspaces.find((w) => w.id === currentId) || null;
  const can = useCallback(
    (action) => canFor(current?.workspace_role, action),
    [current]
  );

  const value = {
    workspaces,
    current,
    currentId,
    loading,
    ready,
    switchWorkspace,
    refresh,
    can,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider');
  return ctx;
};
